<template>
  <div class="hl-overlay hl-overlay--light" @click.self="$emit('close')">
    <div class="hl-player-card">
      <button type="button" class="hl-close" @click="$emit('close')">
        &times;
      </button>

      <div class="hl-player-video">
        <video
          ref="player"
          playsinline
          controlslist="nodownload"
          :poster="video.thumbnail || ''"
          :src="video.videoUrl || ''"
          @loadedmetadata="onLoadedMeta"
          @timeupdate="onTimeUpdate"
          @play="isPlaying = true"
          @pause="isPlaying = false"
          @ended="isPlaying = false"
          @click="togglePlay"
        ></video>

        <div class="hl-controls">
          <button
            type="button"
            class="hl-controls-toggle"
            :aria-label="isPlaying ? 'Pause' : 'Play'"
            @click="togglePlay"
          >
            <svg v-if="!isPlaying" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14l11-7z" fill="currentColor" />
            </svg>
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 4h3.5v16H7zM13.5 4H17v16h-3.5z" fill="currentColor" />
            </svg>
          </button>
          <div class="hl-controls-track" @click="seek">
            <div
              class="hl-controls-fill"
              :style="{ width: progressPercent + '%' }"
            ></div>
          </div>
          <span class="hl-controls-time">
            {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
          </span>
        </div>
      </div>

      <div class="hl-player-info">
        <div class="hl-player-meta">
          <h4>{{ video.title }}</h4>
          <div class="hl-meta">
            <div class="hl-meta-item">
              <img src="/assets/event-highlights/calender.svg" alt="" />
              <span>
                <small>Event Date</small>
                {{ video.eventDate }}
              </span>
            </div>
            <div class="hl-meta-item">
              <img src="/assets/event-highlights/clock.svg" alt="" />
              <span>
                <small>Expiry Date:</small>
                {{ video.expiryDate }}
              </span>
            </div>
          </div>
        </div>
        <div class="hl-player-actions">
          <span v-if="!purchased" class="gems-badge gems-badge-dark">
            <img src="/assets/event-highlights/gems.svg" alt="" />
            {{ video.points }}
          </span>
          <a
            href="javascript:void(0)"
            class="hl-btn hl-btn--download"
            @click="$emit('download', video)"
          >
            <span>Download</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "@/assets/css/event-highlights.css";

export default {
  name: "HighlightVideoPlayer",
  props: {
    video: { type: Object, required: true },
    purchased: { type: Boolean, default: false },
  },
  emits: ["close", "download"],
  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
    };
  },
  computed: {
    progressPercent() {
      return this.duration ? (this.currentTime / this.duration) * 100 : 0;
    },
  },
  methods: {
    togglePlay() {
      const v = this.$refs.player;
      if (!v) return;
      if (v.paused) {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        v.pause();
      }
    },
    onLoadedMeta() {
      this.duration = this.$refs.player ? this.$refs.player.duration || 0 : 0;
    },
    onTimeUpdate() {
      this.currentTime = this.$refs.player
        ? this.$refs.player.currentTime || 0
        : 0;
    },
    seek(evt) {
      const v = this.$refs.player;
      if (!v || !this.duration) return;
      const rect = evt.currentTarget.getBoundingClientRect();
      const ratio = Math.min(
        Math.max((evt.clientX - rect.left) / rect.width, 0),
        1
      );
      v.currentTime = ratio * this.duration;
      this.currentTime = v.currentTime;
    },
    formatTime(sec) {
      if (!sec || isNaN(sec) || !isFinite(sec)) return "0:00";
      const m = Math.floor(sec / 60);
      const s = Math.floor(sec % 60)
        .toString()
        .padStart(2, "0");
      return `${m}:${s}`;
    },
  },
};
</script>

<style scoped>
.hl-player-card {
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: auto;
  padding: 14px;
  border-radius: 20px;
  background: #fff;
}
.hl-player-card .hl-close {
  top: -16px;
  right: -16px;
  background: #fff;
  color: #1a1a1a;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}
.hl-player-video {
  position: relative;
  line-height: 0;
  border-radius: 12px;
  overflow: hidden;
}
.hl-player-video video {
  width: 100%;
  display: block;
  aspect-ratio: 16 / 9;
  background: #000;
  cursor: pointer;
}

/* custom controls bar */
.hl-controls {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  line-height: 1;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0));
}
.hl-controls-toggle {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fff;
  color: #384251;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}
.hl-controls-toggle svg {
  width: 15px;
  height: 15px;
  display: block;
}
.hl-controls-track {
  flex: 1 1 auto;
  height: 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.45);
  cursor: pointer;
  overflow: hidden;
}
.hl-controls-fill {
  width: 0;
  height: 100%;
  border-radius: 3px;
  background: #f0c229;
}
.hl-controls-time {
  flex: 0 0 auto;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

/* footer */
.hl-player-info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px 24px;
  padding: 18px 10px 8px;
}
.hl-player-meta {
  flex: 1 1 240px;
  min-width: 0;
}
.hl-player-meta h4 {
  margin: 0 0 12px;
  font-size: 19px;
  font-weight: 700;
  color: #1a1a1a;
}
.hl-player-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 1 auto;
}
.hl-player-actions .hl-btn {
  flex: 0 0 auto;
  height: 50px;
  border-radius: 25px;
  padding: 0 34px;
  font-size: 18px;
}
.hl-player-actions .gems-badge {
  flex: 0 0 auto;
}

@media (max-width: 575.98px) {
  .hl-player-info {
    flex-direction: column;
    align-items: stretch;
  }
  .hl-player-actions {
    justify-content: space-between;
  }
  .hl-player-actions .hl-btn {
    flex: 1 1 auto;
    padding: 0 20px;
  }
  /* bigger touch target for the controls play/pause on phones */
  .hl-controls-toggle {
    width: 40px;
    height: 40px;
  }
  .hl-controls-toggle svg {
    width: 18px;
    height: 18px;
  }
}
</style>
